import { z } from "zod";

// ============================================
// CARRIER OPTIONS (used by Step 1 component)
// ============================================
export const CARRIER_OPTIONS = [
  "Allstate",
  "American Family",
  "Farmers",
  "GEICO",
  "Liberty Mutual",
  "Nationwide",
  "Progressive",
  "State Farm",
  "Travelers",
  "USAA",
  "Other",
] as const;

// ============================================
// EDUCATION LEVEL OPTIONS
// ============================================
export const EDUCATION_LEVELS = [
  "no_diploma",
  "high_school",
  "some_college",
  "associates",
  "bachelors",
  "masters",
  "doctorate",
] as const;

export const EDUCATION_LABELS: Record<string, string> = {
  no_diploma: "No high school diploma",
  high_school: "High school diploma / GED",
  some_college: "Some college",
  associates: "Associate's degree",
  bachelors: "Bachelor's degree",
  masters: "Master's degree",
  doctorate: "Doctorate / Professional degree",
};

// ============================================
// VEHICLE USE OPTIONS
// ============================================
export const VEHICLE_USE_OPTIONS = [
  "business",
  "commute",
  "pleasure",
  "rideshare",
] as const;

export const VEHICLE_USE_LABELS: Record<string, string> = {
  business: "Business",
  commute: "Commute to work / school",
  pleasure: "Pleasure",
  rideshare: "Rideshare (Uber / Lyft)",
};

// ============================================
// STEP 1: Your Policy
// ============================================
export const step1Schema = z.object({
  carrierName: z.string().min(1, "Please select your insurance carrier"),
  carrierNameOther: z.string().optional(),
  policyNumber: z
    .string()
    .min(5, "Policy numbers are usually at least 5 characters")
    .max(15, "Policy numbers are usually under 15 characters")
    .regex(/^[a-zA-Z0-9]+$/, "Policy number should only contain letters and numbers"),
});

// ============================================
// STEP 2: About You
// ============================================
export const step2Schema = z.object({
  fullLegalName: z
    .string()
    .min(2, "Please enter your full name as it appears on your policy")
    .max(100, "Name seems too long, please check"),
  dateOfBirth: z
    .string()
    .min(1, "Date of birth is required for carrier verification")
    .refine(
      (val) => {
        const dob = new Date(val);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear();
        return age >= 16 && age <= 120;
      },
      { message: "Please enter a valid date of birth (must be at least 16 years old)" }
    ),
  mailingAddressStreet: z.string().min(3, "Please enter your street address"),
  mailingAddressUnit: z.string().optional(),
  mailingAddressCity: z.string().min(1, "City is required"),
  mailingAddressState: z.string().length(2, "Please select your state"),
  mailingAddressZip: z
    .string()
    .regex(/^\d{5}(-\d{4})?$/, "Please enter a valid 5-digit ZIP code"),
  homeOwnership: z.enum(["own", "rent", "other"], {
    errorMap: () => ({ message: "Please indicate whether you own or rent at this address" }),
  }),
  phoneOnFile: z
    .string()
    .regex(/^\+?1?\d{10,11}$/, "Please enter a valid 10-digit phone number"),
  phoneIsCurrent: z.boolean().default(true),
  phoneNew: z
    .string()
    .regex(/^\+?1?\d{10,11}$/, "Please enter a valid 10-digit phone number")
    .optional()
    .or(z.literal("")),
});

// ============================================
// STEP 3: Your Vehicle
// ============================================
// Each vehicle entry (primary and additional) shares this shape. The primary
// vehicle's fields also live on step3Schema directly for backwards
// compatibility with the existing flat DB columns and success-screen reads.
export const vehicleItemSchema = z
  .object({
    vehicleYear: z
      .number()
      .min(1980, "Vehicle year seems too old")
      .max(new Date().getFullYear() + 1, "Vehicle year cannot be in the future"),
    vehicleMake: z.string().min(1, "Please enter the vehicle make"),
    vehicleModel: z.string().min(1, "Please enter the vehicle model"),
    vehiclePurchaseDate: z
      .string()
      .min(1, "Please enter when you purchased this vehicle")
      .regex(/^(0[1-9]|1[0-2])\/\d{4}$/, "Please enter a valid date in MM/YYYY format"),
    vehicleBoughtCondition: z.enum(["new", "used", "certified_pre_owned"], {
      errorMap: () => ({ message: "Please select whether the vehicle was bought new or used" }),
    }),
    vehicleOriginalPurchaseMethod: z.enum(["paid_in_full", "financed", "leased"], {
      errorMap: () => ({ message: "Please select how the vehicle was originally purchased" }),
    }),
    vehicleCurrentPayoffStatus: z.enum(["still_financed", "still_leased", "fully_paid_off"], {
      errorMap: () => ({ message: "Please select the current payoff status" }),
    }),
    vehicleLienHolder: z.string().optional(),
    annualMileage: z
      .number()
      .min(0, "Mileage cannot be negative")
      .max(200000, "Annual mileage seems unusually high, please check"),
    primaryVehicleUse: z.enum(["commute", "pleasure", "business", "rideshare"], {
      errorMap: () => ({ message: "Please select how you primarily use this vehicle" }),
    }),
    commuteMileage: z.number().min(1).max(500).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      (data.vehicleCurrentPayoffStatus === "still_financed" ||
        data.vehicleCurrentPayoffStatus === "still_leased") &&
      (!data.vehicleLienHolder || data.vehicleLienHolder.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter the financing or leasing company name",
        path: ["vehicleLienHolder"],
      });
    }
    if (data.primaryVehicleUse === "commute" && !data.commuteMileage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter your one-way commute distance",
        path: ["commuteMileage"],
      });
    }
  });

export const MAX_ADDITIONAL_VEHICLES = 4;

export const step3Schema = z
  .object({
    vehicleYear: z
      .number()
      .min(1980, "Vehicle year seems too old")
      .max(new Date().getFullYear() + 1, "Vehicle year cannot be in the future"),
    vehicleMake: z.string().min(1, "Please enter the vehicle make"),
    vehicleModel: z.string().min(1, "Please enter the vehicle model"),
    vehiclePurchaseDate: z
      .string()
      .min(1, "Please enter when you purchased this vehicle")
      .regex(/^(0[1-9]|1[0-2])\/\d{4}$/, "Please enter a valid date in MM/YYYY format"),
    vehicleBoughtCondition: z.enum(["new", "used", "certified_pre_owned"], {
      errorMap: () => ({ message: "Please select whether the vehicle was bought new or used" }),
    }),
    vehicleOriginalPurchaseMethod: z.enum(["paid_in_full", "financed", "leased"], {
      errorMap: () => ({ message: "Please select how the vehicle was originally purchased" }),
    }),
    vehicleCurrentPayoffStatus: z.enum(["still_financed", "still_leased", "fully_paid_off"], {
      errorMap: () => ({ message: "Please select the current payoff status" }),
    }),
    vehicleLienHolder: z.string().optional(),
    annualMileage: z
      .number()
      .min(0, "Mileage cannot be negative")
      .max(200000, "Annual mileage seems unusually high, please check"),
    primaryVehicleUse: z.enum(["commute", "pleasure", "business", "rideshare"], {
      errorMap: () => ({ message: "Please select how you primarily use this vehicle" }),
    }),
    commuteMileage: z.number().min(1).max(500).optional(),
    additionalVehicles: z
      .array(vehicleItemSchema)
      .max(MAX_ADDITIONAL_VEHICLES, `Maximum ${MAX_ADDITIONAL_VEHICLES} additional vehicles`)
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (
      (data.vehicleCurrentPayoffStatus === "still_financed" ||
        data.vehicleCurrentPayoffStatus === "still_leased") &&
      (!data.vehicleLienHolder || data.vehicleLienHolder.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter the financing or leasing company name",
        path: ["vehicleLienHolder"],
      });
    }
    if (data.primaryVehicleUse === "commute" && !data.commuteMileage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter your one-way commute distance",
        path: ["commuteMileage"],
      });
    }
  });

export type VehicleItem = z.infer<typeof vehicleItemSchema>;

// ============================================
// STEP 4: Your Details
// ============================================
export const step4Schema = z
  .object({
    licenseState: z.string().length(2, "Please select your license state"),
    yearFirstLicensed: z
      .number()
      .min(1940, "Please enter a valid year")
      .max(new Date().getFullYear(), "Year cannot be in the future"),
    educationLevel: z.enum([...EDUCATION_LEVELS], {
      errorMap: () => ({ message: "Please select your highest level of education" }),
    }),
    maritalStatus: z.enum(
      ["single", "married", "domestic_partner", "divorced", "widowed"],
      { errorMap: () => ({ message: "Please select your marital status" }) }
    ),
    jobTitle: z.string().min(1, "Please enter your current job title"),
    employerName: z.string().min(1, "Please enter your employer name"),
    isStudent: z.boolean().default(false),
    studentSchool: z.string().optional(),
    studentProgram: z.string().optional(),
    studentDegreePursuing: z.string().optional(),
    studentFullTime: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isStudent) {
      if (!data.studentSchool || data.studentSchool.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter your school name",
          path: ["studentSchool"],
        });
      }
      if (!data.studentDegreePursuing || data.studentDegreePursuing.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter the degree you are pursuing",
          path: ["studentDegreePursuing"],
        });
      }
    }
  });

// ============================================
// STEP 5: Co-Insured Drivers (per driver)
// ============================================
export const coInsuredDriverSchema = z
  .object({
    fullLegalName: z.string().min(2, "Please enter the driver's full legal name"),
    dateOfBirth: z
      .string()
      .min(1, "Date of birth is required")
      .refine(
        (val) => {
          const dob = new Date(val);
          const today = new Date();
          const age = today.getFullYear() - dob.getFullYear();
          return age >= 15 && age <= 120;
        },
        { message: "Please enter a valid date of birth" }
      ),
    phoneSameAsPrimary: z.boolean().default(false),
    phone: z.string().optional(),
    addressSameAsPrimary: z.boolean().default(false),
    mailingAddressStreet: z.string().optional(),
    mailingAddressUnit: z.string().optional(),
    mailingAddressCity: z.string().optional(),
    mailingAddressState: z.string().optional(),
    mailingAddressZip: z.string().optional(),
    licenseStateSameAsPrimary: z.boolean().default(false),
    licenseState: z.string().optional(),
    yearFirstLicensed: z.number().optional(),
    educationLevel: z.string().optional(),
    maritalStatusSameAsPrimary: z.boolean().default(false),
    maritalStatus: z.string().optional(),
    jobTitle: z.string().min(1, "Please enter the driver's job title"),
    employerSameAsPrimary: z.boolean().default(false),
    employerName: z.string().optional(),
    isStudent: z.boolean().default(false),
    studentSchool: z.string().optional(),
    studentProgram: z.string().optional(),
    studentDegreePursuing: z.string().optional(),
    studentFullTime: z.boolean().optional(),
    relationshipToPrimary: z.enum(
      ["spouse", "domestic_partner", "child", "parent", "sibling", "other"],
      { errorMap: () => ({ message: "Please select the relationship to the primary policyholder" }) }
    ),
  })
  .refine(
    (data) => {
      if (!data.phoneSameAsPrimary && (!data.phone || data.phone.trim() === "")) {
        return false;
      }
      return true;
    },
    { message: "Please enter a phone number or select 'Same as primary'", path: ["phone"] }
  )
  .refine(
    (data) => {
      if (
        !data.addressSameAsPrimary &&
        (!data.mailingAddressStreet || data.mailingAddressStreet.trim() === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please enter an address or select 'Same as primary'",
      path: ["mailingAddressStreet"],
    }
  )
  .refine(
    (data) => {
      if (
        !data.employerSameAsPrimary &&
        (!data.employerName || data.employerName.trim() === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please enter an employer or select 'Same as primary'",
      path: ["employerName"],
    }
  )
  .refine(
    (data) => {
      if (
        !data.licenseStateSameAsPrimary &&
        (!data.licenseState || data.licenseState.trim() === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please select a license state or select 'Same as primary'",
      path: ["licenseState"],
    }
  )
  .refine(
    (data) => {
      if (
        !data.maritalStatusSameAsPrimary &&
        (!data.maritalStatus || data.maritalStatus.trim() === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please select a marital status or select 'Same as primary'",
      path: ["maritalStatus"],
    }
  )
  .refine(
    (data) => {
      if (data.isStudent && (!data.studentSchool || data.studentSchool.trim() === "")) {
        return false;
      }
      return true;
    },
    { message: "Please enter the school name", path: ["studentSchool"] }
  );

export const step5Schema = z.object({
  hasCoInsured: z.boolean(),
  coInsuredCount: z.number().min(0).max(10).optional(),
  drivers: z.array(coInsuredDriverSchema).optional(),
});

// ============================================
// STEP 6: Your Savings Info
// ============================================
const drivingRecordItemSchema = z.object({
  hasIncident: z.boolean().optional(),
  details: z.string().optional(),
});

export const step6Schema = z
  .object({
    // Required: Premium info (pulled out to top)
    currentPremiumAmount: z.number().positive("Please enter your current premium amount"),
    currentPremiumFrequency: z.enum(["monthly", "six_month", "annual"], {
      errorMap: () => ({ message: "Please select your premium payment frequency" }),
    }),
    emailOnFile: z.string().email("Please enter a valid email address").min(1, "Email is required"),
    useSigninEmail: z.boolean().optional().default(false),

    // Competitive quote (gated)
    hasCompetitiveQuote: z.boolean().optional().default(false),
    competitiveQuoteAmount: z.number().positive().optional(),
    competitiveQuoteCarrier: z.string().optional(),

    // Pricing
    targetPrice: z.number().positive().optional(),

    // Payment & memberships
    hasAutopay: z.boolean().optional(),
    hasPaperless: z.boolean().optional(),
    paysInFull: z.boolean().optional(),
    memberships: z.array(z.string()).optional(),
    membershipsOther: z.string().optional(),

    // Driving record
    drivingRecordAccidents: drivingRecordItemSchema.optional(),
    drivingRecordClaims: drivingRecordItemSchema.optional(),
    drivingRecordTickets: drivingRecordItemSchema.optional(),

    // Safety
    safetyFeatures: z.array(z.string()).optional(),
    safetyFeaturesOther: z.string().optional(),

    // Additional
    defensiveDrivingCompleted: z.boolean().optional(),
    defensiveDrivingDate: z.string().optional(),
    driversLicenseNumber: z.string().optional(),
    otherPolicies: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    // Competitive quote details required if hasCompetitiveQuote
    if (data.hasCompetitiveQuote) {
      if (!data.competitiveQuoteAmount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter the quote amount",
          path: ["competitiveQuoteAmount"],
        });
      }
      if (!data.competitiveQuoteCarrier || data.competitiveQuoteCarrier.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter the carrier name",
          path: ["competitiveQuoteCarrier"],
        });
      }
    }
    // Driving record details required when incident is checked
    if (data.drivingRecordAccidents?.hasIncident && !data.drivingRecordAccidents?.details?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide details about your accidents",
        path: ["drivingRecordAccidents", "details"],
      });
    }
    if (data.drivingRecordClaims?.hasIncident && !data.drivingRecordClaims?.details?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide details about your claims",
        path: ["drivingRecordClaims", "details"],
      });
    }
    if (data.drivingRecordTickets?.hasIncident && !data.drivingRecordTickets?.details?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide details about your tickets",
        path: ["drivingRecordTickets", "details"],
      });
    }
  });

// ============================================
// HONEYPOT (anti-spam, hidden field -- reject if filled)
// ============================================
export const honeypotSchema = z.object({
  website: z.string().max(0, "Spam detected").optional().default(""),
});

// ============================================
// CONSENT (required before final submission)
// ============================================
export const consentSchema = z.object({
  consentCarrierContact: z
    .boolean()
    .refine((val) => val === true, {
      message: "You must authorize OneCalla to contact your carrier to proceed",
    }),
  consentPrivacyPolicy: z
    .boolean()
    .refine((val) => val === true, {
      message: "You must agree to the Privacy Policy to proceed",
    }),
  consentTermsOfService: z
    .boolean()
    .refine((val) => val === true, {
      message: "You must agree to the Terms of Service to proceed",
    }),
  consentCardCharge: z.boolean().optional(),
});

// ============================================
// CROSS-FIELD VALIDATIONS (not blocking, informational)
// ============================================
export type CrossFieldWarning = {
  type: "info" | "savings_opportunity" | "warning";
  field: string;
  message: string;
};

export function getCrossFieldWarnings(formData: Record<string, unknown>): CrossFieldWarning[] {
  const warnings: CrossFieldWarning[] = [];

  const addressState = formData.mailingAddressState as string;
  const licenseState = formData.licenseState as string;
  if (addressState && licenseState && addressState !== licenseState) {
    warnings.push({
      type: "info",
      field: "licenseState",
      message: `We noticed your address is in ${addressState} but your license is in ${licenseState}. This may affect your premium. We'll review this with your carrier.`,
    });
  }

  const originalMethod = formData.vehicleOriginalPurchaseMethod as string;
  const currentStatus = formData.vehicleCurrentPayoffStatus as string;
  if (
    (originalMethod === "financed" || originalMethod === "leased") &&
    currentStatus === "fully_paid_off"
  ) {
    const method = originalMethod === "leased" ? "leased" : "financed";
    warnings.push({
      type: "info",
      field: "vehicleCurrentPayoffStatus",
      message: `Your vehicle is paid off but was originally ${method}. We'll make sure your policy reflects this correctly.`,
    });
  }

  const dob = formData.dateOfBirth as string;
  if (dob) {
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    if (age === 25 || age === 26) {
      warnings.push({
        type: "info",
        field: "dateOfBirth",
        message:
          "We've noted your age for our review with your carrier.",
      });
    }
  }

  return warnings;
}

// ============================================
// FULL FORM SCHEMA (all steps combined)
// ============================================
export const fullFormSchema = z.object({
  step1: step1Schema,
  step2: step2Schema,
  step3: step3Schema,
  step4: step4Schema,
  step5: step5Schema,
  step6: step6Schema,
  consent: consentSchema,
  honeypot: honeypotSchema,
});

export type FullFormData = z.infer<typeof fullFormSchema>;
